import React from 'react';
import { Box, Typography } from '@mui/material';
import RelatedSectionShell from './RelatedSectionShell';
import { TilesGrid } from './RelatedSectionShell';
import ImpressionTile from '../tiles/ImpressionTile';
import PositivesTable from './tables/PositivesTable';
import NegativesTable from './tables/NegativesTable';
import { slugify } from '../../utils/slug';

/* ---------- helpers ---------- */

function pickJobNode(story, jobName) {
  const arr = Array.isArray(story?.jobs_to_be_done) ? story.jobs_to_be_done : [];
  const target = slugify(jobName || '');
  return arr.find(j => slugify(j?.job_to_be_done_category || '') === target) || null;
}

function safeNum(n, d = 0) {
  const x = Number(n);
  return Number.isFinite(x) ? x : d;
}

function buildImpressionTiles({ customerStories = [], jobName, kind }) {
  // kind: 'positive' | 'negative'
  const isPos = kind === 'positive';
  const arrayKey = isPos ? 'positives_of_solution_choice' : 'drawbacks_of_solution_choice';
  const catKey   = isPos ? 'positive_category'            : 'drawback_category';
  const textKey  = isPos ? 'positive'                     : 'drawback';
  const scoreKey = isPos ? 'positive_importance'          : 'drawback_importance';
  const quoteKey = isPos ? 'positive_importance_quote'    : 'drawback_importance_quote';
  const tsKey    = isPos ? 'positive_timestamps'          : 'drawback_timestamps';

  // Only consider interviews that contain this Job
  const scopedStories = (customerStories || []).filter(s => !!pickJobNode(s, jobName));

  // Group by category
  const groups = new Map(); // slug -> { title, items: [...], receipts: [...] }
  for (const s of scopedStories) {
    const arr = Array.isArray(s?.[arrayKey]) ? s[arrayKey] : [];
    for (const it of arr) {
      const cat = it?.[catKey] || 'Uncategorized';
      const key = slugify(cat);
      if (!groups.has(key)) groups.set(key, { title: cat, items: [], receipts: [] });
      groups.get(key).items.push(it);

      const q = it?.[quoteKey] || it?.quote || '';
      if (q) groups.get(key).receipts.push({ quote: q, timestamp: it?.[tsKey] || null });
    }
  }

  // Build tiles
  const tiles = [];
  for (const [slug, g] of groups.entries()) {
    const count = g.items.length;
    const avgImportance =
      count ? (g.items.reduce((sum, it) => sum + safeNum(it?.[scoreKey], 0), 0) / count) : 0;

    // Tile receipts: dedupe by quote text, keep first few
    const seen = new Set();
    const receipts = [];
    for (const r of g.receipts) {
      const text = String(r.quote || '').trim();
      if (!text || seen.has(text)) continue;
      seen.add(text);
      receipts.push({ quote: text, timestamp: r.timestamp || null });
      if (receipts.length >= 5) break; // keep it tight
    }

    tiles.push({
      key: slug,
      title: g.title,
      count,
      // ImpressionTile expects:
      variant: isPos ? 'positive' : 'negative',
      score: avgImportance,      // drives the Positivity/Negativity pill; using avg importance is stable
      importance: avgImportance, // chip in the metrics row
      receipts,                  // shown by ReceiptsList
    });
  }

  // Rank to match dashboard feel: by occurrences desc, then by avg importance desc, then title
  tiles.sort((a, b) =>
    b.count - a.count ||
    (b.importance || 0) - (a.importance || 0) ||
    String(a.title).localeCompare(String(b.title))
  );

  // Section total = # of interviews (scoped to this job) that had at least one item in this bucket
  const sectionTotal = new Set(
    scopedStories
      .filter(s => (Array.isArray(s?.[arrayKey]) ? s[arrayKey] : []).length > 0)
      .map(s => s.customer_name || s.filename || JSON.stringify(s))
  ).size;

  return { tiles, sectionTotal };
}

/* ---------- component ---------- */

export default function RelatedImpressions({ customerStories = [], jobName }) {
  // Build from raw Customer Stories; don’t rely on dashboard section discovery
  const memo = React.useMemo(() => {
    const pos = buildImpressionTiles({ customerStories, jobName, kind: 'positive' });
    const neg = buildImpressionTiles({ customerStories, jobName, kind: 'negative' });

    // Prefer a unified container title/description like your other shells
    const section = {
      title: 'Impressions',
      description: 'What customers like and dislike about current solutions (scoped to this Job).',
      section_total_interviews: Math.max(pos.sectionTotal, neg.sectionTotal)
    };

    return {
      section,
      posTiles: pos.tiles,
      negTiles: neg.tiles,
      sectionTotal: section.section_total_interviews
    };
  }, [customerStories, jobName]);

  const { section, posTiles, negTiles, sectionTotal } = memo;

  // If nothing to show, bail out
  if ((!posTiles.length) && (!negTiles.length)) return null;

  const top3Pos = posTiles.slice(0, 3);
  const top3Neg = negTiles.slice(0, 3);

  return (
    <RelatedSectionShell section={section}>
      {/* Top-3 Positive Impressions */}
      {top3Pos.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            Top positive impressions
          </Typography>
          <TilesGrid
            tiles={top3Pos}
            renderTile={(t) => <ImpressionTile {...t} sectionTotal={sectionTotal} />}
          />
        </Box>
      )}

      {/* Top-3 Negative Impressions */}
      {top3Neg.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            Top negative impressions
          </Typography>
          <TilesGrid
            tiles={top3Neg}
            renderTile={(t) => <ImpressionTile {...t} sectionTotal={sectionTotal} />}
          />
        </Box>
      )}

      {/* Full ranked tables (order preserved from our tiles) */}
      {posTiles.length > 0 && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            Positive impressions
          </Typography>
          <PositivesTable
            rowsSource={posTiles}
            customerStories={customerStories}
            jobName={jobName}
          />
        </Box>
      )}

      {negTiles.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            Negative impressions
          </Typography>
          <NegativesTable
            rowsSource={negTiles}
            customerStories={customerStories}
            jobName={jobName}
          />
        </Box>
      )}
    </RelatedSectionShell>
  );
}
